import React from "react";
import { CameraEnhancer, CameraView } from "dynamsoft-camera-enhancer";
import { CaptureVisionRouter } from "dynamsoft-capture-vision-router";
import { MultiFrameResultCrossFilter } from "dynamsoft-utility";
import { EnumBarcodeFormat } from "dynamsoft-barcode-reader-bundle";
import { Toast } from 'antd-mobile';

const componentDestroyedErrorMsg = "VideoCapture Component Destroyed";

/** 扫描关键帧体积上限（字节），超过则自动缩小尺寸或降低质量直到满足。 */
const MAX_FRAME_BYTES = 100 * 1024;


/**
 * 直接压缩至0.65质量。
 */
function canvasToMaxSizeDataURL(canvas: HTMLCanvasElement, maxBytes: number): string {
  const firstTry = canvas.toDataURL("image/jpeg", 0.65);
  return firstTry;
}

interface VideoCaptureProps {
  onScanSuccess: (text: string, frameDataUrl?: string) => void;
  onScanError?: (error: Error) => void;
  isPaused?: boolean;
}

class VideoCapture extends React.Component<VideoCaptureProps> {
  cameraViewContainer: React.RefObject<HTMLDivElement> = React.createRef();

  resolveInit?: () => void;
  pInit: Promise<void> = new Promise((r) => (this.resolveInit = r));
  isDestroyed = false;

  cvRouter?: CaptureVisionRouter;
  cameraEnhancer?: CameraEnhancer;
  cameraView?: any; // 存储 CameraView 实例以便销毁
  isScanning = false;

  private scanDebounceMs = 650; // 650ms对焦时间，每次扫描都需要停留650ms
  private pendingScanCode: string | null = null; // 当前正在确认的条码
  private pendingScanTimeout?: NodeJS.Timeout; // 确认的定时器

  async componentDidMount() {
    try {
      // Create a `CameraEnhancer` instance for camera control and a `CameraView` instance for UI control.
      this.cameraView = await CameraView.createInstance();
      if (this.isDestroyed) {
        throw Error(componentDestroyedErrorMsg);
      } // Check if component is destroyed after every async

      //Ensure that the camera is tiled on the canvas
      this.cameraView.setVideoFit('cover');

      this.cameraEnhancer = await CameraEnhancer.createInstance(this.cameraView);
      if (this.isDestroyed) {
        throw Error(componentDestroyedErrorMsg);
      }

      // Get default UI and append it to DOM.
      const uiElement = this.cameraView.getUIElement();
      if (uiElement.shadowRoot) {
        uiElement.shadowRoot.querySelector(".dce-sel-camera")?.remove();
        uiElement.shadowRoot.querySelector(".dce-sel-resolution")?.remove();
        uiElement.shadowRoot.querySelector(".dce-msg-poweredby")?.remove();
      }
      this.cameraViewContainer.current!.append(uiElement);

      // Create a `CaptureVisionRouter` instance and set `CameraEnhancer` instance as its image source.
      this.cvRouter = await CaptureVisionRouter.createInstance();
      if (this.isDestroyed) {
        throw Error(componentDestroyedErrorMsg);
      }
      this.cvRouter.setInput(this.cameraEnhancer);

      let settings = await this.cvRouter.getSimplifiedSettings("ReadSingleBarcode");
      settings.barcodeSettings.barcodeFormatIds = EnumBarcodeFormat.BF_QR_CODE | EnumBarcodeFormat.BF_CODE_128;
      await this.cvRouter.updateSettings("ReadSingleBarcode", settings);
      await this.cvRouter.startCapturing("ReadSingleBarcode");


      // Define a callback for results.
      this.cvRouter.addResultReceiver({
        onDecodedBarcodesReceived: (result) => {
          if (!result.barcodeResultItems.length) return;

          const scanText = result.barcodeResultItems[0].text;
          
          // 🔍 如果是同一个码，继续等待（不重置定时器）
          if (this.pendingScanCode === scanText) {
            // 正在等待确认中，不做任何操作
            return;
          }
          
          // 📌 检测到新的条码
          console.log("🔍 Barcode detected:", scanText, "- Hold for 1 second to confirm");
          
          // 清除之前的定时器
          if (this.pendingScanTimeout) {
            clearTimeout(this.pendingScanTimeout);
          }
          
          // 记录当前正在确认的条码
          this.pendingScanCode = scanText;
          
          // 设置1秒后确认
          this.pendingScanTimeout = setTimeout(() => {
            console.log("✅ SCAN CONFIRMED:", scanText);
            let frameDataUrl: string | undefined;
            try {
              const ce = this.cameraEnhancer as unknown as { fetchImage?: () => { toCanvas?: () => HTMLCanvasElement } };
              const frame = ce?.fetchImage?.();
              if (frame?.toCanvas) {
                const canvas = frame.toCanvas();
                frameDataUrl = canvasToMaxSizeDataURL(canvas, MAX_FRAME_BYTES);
              }
            } catch (_) {}
            this.props.onScanSuccess(scanText, frameDataUrl);
            // 重置状态
            this.pendingScanCode = null;
            this.pendingScanTimeout = undefined;
          }, this.scanDebounceMs);
        },
      });

      // Filter out unchecked and duplicate results.
      const filter = new MultiFrameResultCrossFilter();
      // Filter out unchecked barcodes.
      filter.enableResultCrossVerification("barcode", true);
      // Filter out duplicate barcodes within 3 seconds.
      filter.enableResultDeduplication("barcode", true);
      await this.cvRouter.addResultFilter(filter);
      if (this.isDestroyed) {
        throw Error(componentDestroyedErrorMsg);
      }

      // Open camera and start scanning single barcode.
      await this.cameraEnhancer.open();
      this.cameraView.setScanLaserVisible(true);
      if (this.isDestroyed) {
        throw Error(componentDestroyedErrorMsg);
      }
      
      if (!this.props.isPaused) {
        await this.startScanning();
      }
    } catch (ex: any) {
      if ((ex as Error)?.message === componentDestroyedErrorMsg) {
        console.log(componentDestroyedErrorMsg);
      } else if (this.isDestroyed) {
        // 组件销毁期间 Dynamsoft 内部抛出的错误（如页面导航后 DOM 已移除），静默忽略
        console.log('VideoCapture: init error after destroy (ignored):', (ex as Error)?.message);
      } else {
        let errMsg = ex.message || ex;
        console.error(errMsg);
        if (this.props.onScanError) {
          this.props.onScanError(new Error(errMsg));
        } else {
            Toast.show({
                content: errMsg,
                position: 'center',
            });
        }
      }
    }

    // Resolve pInit promise once initialization is complete.
    this.resolveInit!();
  }

  async startScanning() {
    if (!this.isScanning && this.cvRouter && !this.isDestroyed) {
      try {
        await this.cvRouter.startCapturing("ReadSingleBarcode");
        this.isScanning = true;
        console.log("Scanning has started");
      } catch (error) {
        console.error("Failed to start scanning:", error);
      }
    }
  }

  async pauseScanning() {
    if (this.isScanning && this.cvRouter && !this.isDestroyed) {
      try {
        await this.cvRouter.stopCapturing();
        this.isScanning = false;
        console.log("Scanning paused");
      } catch (error) {
        console.error("Pause scan failed:", error);
      }
    }
  }

  async componentDidUpdate(prevProps: VideoCaptureProps) {
    if (prevProps.isPaused !== this.props.isPaused) {
      if (this.props.isPaused) {
        await this.pauseScanning();
      } else {
        await this.startScanning();
      }
    }
  }

  async componentWillUnmount() {
    console.log('VideoCapture componentWillUnmount - starting cleanup');
    this.isDestroyed = true;
    
    // 清理待处理的扫描定时器
    if (this.pendingScanTimeout) {
      clearTimeout(this.pendingScanTimeout);
      this.pendingScanTimeout = undefined;
    }
    this.pendingScanCode = null;
    
    try {
      // Wait for the pInit to complete before disposing resources.
      await this.pInit;
      
      // 停止捕获
      if (this.isScanning && this.cvRouter) {
        console.log('VideoCapture: stopping capture');
        await this.cvRouter.stopCapturing();
        this.isScanning = false;
      }
      
      // 关闭相机
      if (this.cameraEnhancer) {
        console.log('VideoCapture: closing camera');
        try {
          await this.cameraEnhancer.close();
        } catch (e) {
          console.warn('Error closing camera:', e);
        }
      }
      
      // 释放资源 - 按照正确的顺序
      if (this.cvRouter) {
        console.log('VideoCapture: disposing cvRouter');
        this.cvRouter.dispose();
        this.cvRouter = undefined;
      }
      
      if (this.cameraEnhancer) {
        console.log('VideoCapture: disposing cameraEnhancer');
        this.cameraEnhancer.dispose();
        this.cameraEnhancer = undefined;
      }
      
      if (this.cameraView) {
        console.log('VideoCapture: disposing cameraView');
        try {
          // 清理 UI 元素
          const uiElement = this.cameraView.getUIElement();
          if (uiElement && uiElement.parentElement) {
            uiElement.parentElement.removeChild(uiElement);
          }
          // 销毁 cameraView
          if (typeof this.cameraView.dispose === 'function') {
            this.cameraView.dispose();
          }
          this.cameraView = undefined;
        } catch (e) {
          console.warn('Error disposing cameraView:', e);
        }
      }
      
      console.log('VideoCapture componentWillUnmount - cleanup complete');
    } catch (e) {
      console.error('Error disposing VideoCapture:', e);
    }
  }

  shouldComponentUpdate(nextProps: VideoCaptureProps) {
    return nextProps.isPaused !== this.props.isPaused;
  }

  render() {
    return (
        <div ref={this.cameraViewContainer} style={{  width: "100%", height: "100%" }}></div>
    );
  }
}

export default VideoCapture;
